// OfferIntakeLinkButton - KOPS-711
// Generates (or fetches) the per-listing public offer link and copies it to the
// clipboard. Used in the deal Overview section and the Offers panel.

import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaLink, FaCheck } from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';

interface OfferIntakeLinkButtonProps {
  transactionId: string;
  size?: 'sm' | 'lg';
  variant?: string;
  label?: string;
}

const OfferIntakeLinkButton: React.FC<OfferIntakeLinkButtonProps> = ({
  transactionId,
  size = 'sm',
  variant = 'outline-primary',
  label = 'Copy Offer Link'
}) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      const res = await axiosInstance.post(`/offer-intake/links/${transactionId}`);
      if (res.data?.success) {
        // Public page lives on the brokerage website (chat.realestate), not the app
        const site = window.location.hostname.startsWith('dev.') || window.location.hostname === 'localhost'
          ? 'https://dev.chat.realestate'
          : 'https://www.chat.realestate';
        const url = `${site}${res.data.path}`;
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Offer link copied — share it with buyer\u2019s agents. Their submissions land on this deal automatically.');
        setTimeout(() => setCopied(false), 3000);
      } else {
        toast.error(res.data?.error || 'Failed to create offer link');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create offer link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant={copied ? 'success' : variant} size={size} onClick={handleClick} disabled={busy}>
      {busy ? <Spinner animation="border" size="sm" /> : copied ? (<><FaCheck className="me-1" /> Copied</>) : (<><FaLink className="me-1" /> {label}</>)}
    </Button>
  );
};

export default OfferIntakeLinkButton;
